import { BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';
import { type TokenProvider } from '@/app/providers/SsoTokenProvider';
import { loginRequest } from '@/shared/config/msalConfig';
import { getMsalInstance } from '@/shared/lib/msalInstance';

// sessionStorage 키 — 세션 내 redirect 진행 중 여부를 추적
const REDIRECT_PENDING_KEY = 'msal_redirect_pending';

// module-level 플래그 — 동일 JS 실행 컨텍스트(React Strict Mode 이중 호출 등)에서 중복 redirect 방지
let isRedirectInProgress = false;

class MsalTokenProvider implements TokenProvider {
  private readyPromise: Promise<void> | null = null;

  /**
   * MSAL 초기화 + redirect 콜백 처리.
   * 멱등성 보장 — 여러 번 호출해도 initialize는 한 번만 실행.
   */
  ready(): Promise<void> {
    if (!this.readyPromise) {
      const pca = getMsalInstance();
      this.readyPromise = pca
        .initialize()
        .then(async () => {
          const result = await pca.handleRedirectPromise();
          if (result?.account) {
            pca.setActiveAccount(result.account);
            // redirect 완료 → 플래그 해제
            sessionStorage.removeItem(REDIRECT_PENDING_KEY);
          } else if (sessionStorage.getItem(REDIRECT_PENDING_KEY)) {
            // redirect를 시도했으나 계정이 없음 → 루프 감지
            sessionStorage.removeItem(REDIRECT_PENDING_KEY);
            throw new Error('Microsoft 로그인이 완료되지 않았습니다. 다시 시도해 주세요.');
          }
        })
        .catch((err: unknown) => {
          this.readyPromise = null;
          throw err;
        });
    }
    return this.readyPromise;
  }

  async getToken(): Promise<string> {
    await this.ready();

    const pca = getMsalInstance();
    const account = pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? null;

    if (account) {
      try {
        const result = await pca.acquireTokenSilent({ ...loginRequest, account });
        return result.accessToken;
      } catch (e) {
        // silent 실패 시 redirect fallback 대상:
        // - InteractionRequiredAuthError: 사용자 상호작용 필요 (동의, MFA 등)
        // - BrowserAuthError timed_out: hidden iframe이 Azure AD 응답을 받지 못함
        //   (third-party cookie 차단, 개발 환경 등)
        const shouldRedirect =
          e instanceof InteractionRequiredAuthError ||
          (e instanceof BrowserAuthError && e.errorCode === 'timed_out');

        if (shouldRedirect) {
          if (isRedirectInProgress) return new Promise<string>(() => {});
          isRedirectInProgress = true;
          sessionStorage.setItem(REDIRECT_PENDING_KEY, '1');
          try {
            await pca.acquireTokenRedirect({ ...loginRequest, account });
          } catch (redirectErr) {
            isRedirectInProgress = false;
            sessionStorage.removeItem(REDIRECT_PENDING_KEY);
            throw redirectErr;
          }
          return new Promise<string>(() => {});
        }
        throw e;
      }
    }

    // 계정 없음 → MS 로그인 페이지로 redirect
    if (isRedirectInProgress) return new Promise<string>(() => {});
    isRedirectInProgress = true;
    sessionStorage.setItem(REDIRECT_PENDING_KEY, '1');
    try {
      await pca.loginRedirect(loginRequest);
    } catch (redirectErr) {
      isRedirectInProgress = false;
      sessionStorage.removeItem(REDIRECT_PENDING_KEY);
      throw redirectErr;
    }
    return new Promise<string>(() => {});
  }
}

let instance: MsalTokenProvider | null = null;

export function getMsalTokenProvider(): MsalTokenProvider {
  if (!instance) {
    instance = new MsalTokenProvider();
  }
  return instance;
}
